import React from 'react'
import { Theme } from '@material-ui/core'
import { ProjectAnalytics } from '../../../hooks/useEZFolioProject'
import { COLUMN_DEFINITIONS_HEADER_TOOLTIP } from './projectUnitColumnDefinitions'
import { Badge, TableCell, useTheme } from '@mui/material'
import { IconButton } from '@mui/material'
import { Tooltip } from '@mui/material'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import { formatCalendarDateOnly } from '../../../helpers'

export type SortKind =
    | 'concern'
    | 'text'
    | 'date'
    | 'count'
    | 'money'
    | 'percent'
    | 'variance'

export type SortOptionLabels = { high: string; low: string }

export function getActiveSortLabel(
    header: TableHeader,
    orderBy: string,
): string | null {
    if (!header.sortKind || !header.sortAsc || !header.sortDesc) {
        return null
    }
    const labels = getSortOptionLabels(header.sortKind)
    if (orderBy === header.sortDesc) {
        return labels.high
    }
    if (orderBy === header.sortAsc) {
        return labels.low
    }
    return null
}

export function getSortOptionLabels(kind: SortKind): SortOptionLabels {
    switch (kind) {
        case 'concern':
            return {
                high: 'Highest concern first',
                low: 'Lowest concern first',
            }
        case 'text':
            return { high: 'Z → A', low: 'A → Z' }
        case 'date':
            return { high: 'Newest first', low: 'Oldest first' }
        case 'count':
            return { high: 'Highest first', low: 'Lowest first' }
        case 'money':
            return { high: 'Largest first', low: 'Smallest first' }
        case 'percent':
            return { high: 'Highest % first', low: 'Lowest % first' }
        case 'variance':
            return {
                high: 'Under budget first',
                low: 'Over budget first',
            }
    }
}

export interface TableHeader {
    display: string
    field: keyof ProjectAnalytics
    viewMode: 'serviceOrders' | 'budget' | 'all'
    minWidth: number
    sortAsc: string | null
    sortDesc: string | null
    sortKind?: SortKind
    tooltip: string
}

// Sort constants
export const ProjectNameASC = 'ProjectNameASC'
export const ProjectNameDESC = 'ProjectNameDESC'
export const StartDateASC = 'StartDateASC'
export const StartDateDESC = 'StartDateDESC'
export const EndDateASC = 'EndDateASC'
export const EndDateDESC = 'EndDateDESC'
export const ApartmentNameASC = 'ApartmentNameASC'
export const ApartmentNameDESC = 'ApartmentNameDESC'
export const ConcernLevelASC = 'ConcernLevelASC'
export const ConcernLevelDESC = 'ConcernLevelDESC'

// project analytics table sort constants
export const TotalWorkordersASC = 'TotalWorkordersASC'
export const TotalWorkordersDESC = 'TotalWorkordersDESC'
export const NotStartedASC = 'NotStartedASC'
export const NotStartedDESC = 'NotStartedDESC'
export const InProgressASC = 'InProgressASC'
export const InProgressDESC = 'InProgressDESC'
export const CompleteASC = 'CompleteASC'
export const CompleteDESC = 'CompleteDESC'
export const ApprovedASC = 'ApprovedASC'
export const ApprovedDESC = 'ApprovedDESC'
export const InvoicedASC = 'InvoicedASC'
export const InvoicedDESC = 'InvoicedDESC'
export const InvoiceRequestASC = 'InvoiceRequestASC'
export const InvoiceRequestDESC = 'InvoiceRequestDESC'
export const InvoicedOutASC = 'InvoicedOutASC'
export const InvoicedOutDESC = 'InvoicedOutDESC'
export const CaiCountASC = 'CaiCountASC'
export const CaiCountDESC = 'CaiCountDESC'
export const UnassignedUnitsASC = 'UnassignedUnitsASC'
export const UnassignedUnitsDESC = 'UnassignedUnitsDESC'
export const UnitsReadyASC = 'UnitsReadyASC'
export const UnitsReadyDESC = 'UnitsReadyDESC'
export const UnitsScheduledASC = 'UnitsScheduledASC'
export const UnitsScheduledDESC = 'UnitsScheduledDESC'
export const UnitTurnPctASC = 'UnitTurnPctASC'
export const UnitTurnPctDESC = 'UnitTurnPctDESC'
export const MaxVendorOutputASC = 'MaxVendorOutputASC'
export const MaxVendorOutputDESC = 'MaxVendorOutputDESC'
export const BedSpacesASC = 'BedSpacesASC'
export const BedSpacesDESC = 'BedSpacesDESC'
export const CostPerUnitASC = 'CostPerUnitASC'
export const CostPerUnitDESC = 'CostPerUnitDESC'
export const CostPerOccupiableAreaASC = 'CostPerOccupiableAreaASC'
export const CostPerOccupiableAreaDESC = 'CostPerOccupiableAreaDESC'
export const BaselineBudgetASC = 'BaselineBudgetASC'
export const BaselineBudgetDESC = 'BaselineBudgetDESC'
export const TotalCostASC = 'TotalCostASC'
export const TotalCostDESC = 'TotalCostDESC'
export const BudgetVarianceASC = 'BudgetVarianceASC'
export const BudgetVarianceDESC = 'BudgetVarianceDESC'
export const ApprovedChangeOrdersASC = 'ApprovedChangeOrdersASC'
export const ApprovedChangeOrdersDESC = 'ApprovedChangeOrdersDESC'
export const ApprovedChangeOrderCostASC = 'ApprovedChangeOrderCostASC'
export const ApprovedChangeOrderCostDESC = 'ApprovedChangeOrderCostDESC'
export const PendingChangeOrdersASC = 'PendingChangeOrdersASC'
export const PendingChangeOrdersDESC = 'PendingChangeOrdersDESC'
export const PendingChangeOrderCostASC = 'PendingChangeOrderCostASC'
export const PendingChangeOrderCostDESC = 'PendingChangeOrderCostDESC'

// workspace project table headers

export const TotalCreatedASC = 'TotalCreatedASC'
export const TotalCreatedDESC = 'TotalCreatedDESC'
export const ActiveASC = 'ActiveASC'
export const ActiveDESC = 'ActiveDESC'
export const BehindASC = 'BehindASC'
export const BehindDESC = 'BehindDESC'
export const FutureASC = 'FutureASC'
export const FutureDESC = 'FutureDESC'
export const ActiveProjectsASC = 'ActiveProjectsASC'
export const ActiveProjectsDESC = 'ActiveProjectsDESC'
export const FutureProjectsASC = 'FutureProjectsASC'
export const FutureProjectsDESC = 'FutureProjectsDESC'
export const RecentASC = 'RecentASC'
export const RecentDESC = 'RecentDESC'

export const TableHeaders: TableHeader[] = [
    {
        display: 'Concern Level',
        field: 'concern_level',
        viewMode: 'all',
        sortAsc: ConcernLevelASC,
        sortDesc: ConcernLevelDESC,
        sortKind: 'concern',
        minWidth: 100,
        tooltip: 'Concern Level',
    },
    {
        display: 'Apartment Name',
        field: 'apartment_name',
        viewMode: 'all',
        sortAsc: ApartmentNameASC,
        sortDesc: ApartmentNameDESC,
        sortKind: 'text',
        minWidth: 150,
        tooltip: 'Apartment Name',
    },
    {
        display: 'Project Name',
        field: 'name',
        viewMode: 'all',
        sortAsc: ProjectNameASC,
        sortDesc: ProjectNameDESC,
        sortKind: 'text',
        minWidth: 150,
        tooltip: 'Project Name',
    },
    {
        display: 'Start Date',
        field: 'start_date',
        viewMode: 'all',
        sortAsc: StartDateASC,
        sortDesc: StartDateDESC,
        sortKind: 'date',
        minWidth: 100,
        tooltip: 'Start Date',
    },
    {
        display: 'End Date',
        field: 'end_date',
        viewMode: 'all',
        sortAsc: EndDateASC,
        sortDesc: EndDateDESC,
        sortKind: 'date',
        minWidth: 100,
        tooltip: 'End Date',
    },
    {
        display: 'Total Workorders',
        field: 'total_workorders',
        viewMode: 'serviceOrders',
        sortAsc: TotalWorkordersASC,
        sortDesc: TotalWorkordersDESC,
        sortKind: 'count',
        minWidth: 90,
        tooltip: 'Total Workorders',
    },
    {
        display: 'Behind',
        field: 'behind_count',
        viewMode: 'serviceOrders',
        sortAsc: BehindASC,
        sortDesc: BehindDESC,
        sortKind: 'count',
        minWidth: 70,
        tooltip: 'Behind',
    },
    {
        display: 'Not Started',
        field: 'not_started',
        viewMode: 'serviceOrders',
        sortAsc: NotStartedASC,
        sortDesc: NotStartedDESC,
        sortKind: 'count',
        minWidth: 70,
        tooltip: 'Not Started',
    },
    {
        display: 'In Progress',
        field: 'in_progress_count',
        viewMode: 'serviceOrders',
        sortAsc: InProgressASC,
        sortDesc: InProgressDESC,
        sortKind: 'count',
        minWidth: 70,
        tooltip: 'In Progress',
    },
    {
        display: 'Complete',
        field: 'complete_count',
        viewMode: 'serviceOrders',
        sortAsc: CompleteASC,
        sortDesc: CompleteDESC,
        sortKind: 'count',
        minWidth: 70,
        tooltip: 'Complete',
    },
    {
        display: 'Approved',
        field: 'approved_count',
        viewMode: 'serviceOrders',
        sortAsc: ApprovedASC,
        sortDesc: ApprovedDESC,
        sortKind: 'count',
        minWidth: 70,
        tooltip: 'Approved',
    },
    {
        display: 'Invoiced',
        field: 'invoiced_count',
        viewMode: 'serviceOrders',
        sortAsc: InvoicedASC,
        sortDesc: InvoicedDESC,
        sortKind: 'count',
        minWidth: 70,
        tooltip: 'Invoiced',
    },
    {
        display: 'Invoice Request',
        field: 'invoice_request_count',
        viewMode: 'serviceOrders',
        sortAsc: InvoiceRequestASC,
        sortDesc: InvoiceRequestDESC,
        sortKind: 'count',
        minWidth: 90,
        tooltip: 'Invoice Request',
    },
    {
        display: 'Invoiced Out',
        field: 'invoiced_out_count',
        viewMode: 'serviceOrders',
        sortAsc: InvoicedOutASC,
        sortDesc: InvoicedOutDESC,
        sortKind: 'count',
        minWidth: 70,
        tooltip: 'Invoiced Out',
    },
    {
        display: 'CAI',
        field: 'cai_count',
        viewMode: 'serviceOrders',
        sortAsc: CaiCountASC,
        sortDesc: CaiCountDESC,
        sortKind: 'count',
        minWidth: 70,
        tooltip: 'Completed, Approved, Invoiced, and Invoiced Out',
    },
    {
        display: 'Units Unassigned',
        field: 'unassigned_units',
        viewMode: 'serviceOrders',
        sortAsc: UnassignedUnitsASC,
        sortDesc: UnassignedUnitsDESC,
        sortKind: 'count',
        minWidth: 80,
        tooltip: COLUMN_DEFINITIONS_HEADER_TOOLTIP,
    },
    {
        display: 'Units Ready',
        field: 'units_ready',
        viewMode: 'serviceOrders',
        sortAsc: UnitsReadyASC,
        sortDesc: UnitsReadyDESC,
        sortKind: 'percent',
        minWidth: 80,
        tooltip: COLUMN_DEFINITIONS_HEADER_TOOLTIP,
    },
    {
        display: 'Units Scheduled',
        field: 'units_scheduled',
        viewMode: 'serviceOrders',
        sortAsc: UnitsScheduledASC,
        sortDesc: UnitsScheduledDESC,
        sortKind: 'percent',
        minWidth: 80,
        tooltip: COLUMN_DEFINITIONS_HEADER_TOOLTIP,
    },
    {
        display: 'Unit Turn %',
        field: 'unit_turn_pct',
        viewMode: 'serviceOrders',
        sortAsc: UnitTurnPctASC,
        sortDesc: UnitTurnPctDESC,
        sortKind: 'percent',
        minWidth: 90,
        tooltip: COLUMN_DEFINITIONS_HEADER_TOOLTIP,
    },
    {
        display: 'Max Vendor Output',
        field: 'max_vendor_output',
        viewMode: 'serviceOrders',
        sortAsc: MaxVendorOutputASC,
        sortDesc: MaxVendorOutputDESC,
        sortKind: 'count',
        minWidth: 110,
        tooltip: COLUMN_DEFINITIONS_HEADER_TOOLTIP,
    },
    {
        display: 'Bed Spaces',
        field: 'unique_occupiable_areas',
        viewMode: 'budget',
        sortAsc: BedSpacesASC,
        sortDesc: BedSpacesDESC,
        sortKind: 'count',
        minWidth: 90,
        tooltip: 'Total occupiable areas with a workorder',
    },
    {
        display: 'Cost / Unit',
        field: 'cost_per_unit',
        viewMode: 'budget',
        sortAsc: CostPerUnitASC,
        sortDesc: CostPerUnitDESC,
        sortKind: 'money',
        minWidth: 90,
        tooltip: 'Forecasted Cost / Units with a Workorder',
    },
    {
        display: 'Cost / Bed Space',
        field: 'cost_per_occupiable_area',
        viewMode: 'budget',
        sortAsc: CostPerOccupiableAreaASC,
        sortDesc: CostPerOccupiableAreaDESC,
        sortKind: 'money',
        minWidth: 90,
        tooltip: 'Forecasted Cost / Bed Space',
    },
    {
        display: 'Total Budget',
        field: 'baseline_budget',
        viewMode: 'budget',
        sortAsc: BaselineBudgetASC,
        sortDesc: BaselineBudgetDESC,
        sortKind: 'money',
        minWidth: 110,
        tooltip: 'Total budget',
    },
    {
        display: 'Forecasted Cost',
        field: 'total_cost',
        viewMode: 'budget',
        sortAsc: TotalCostASC,
        sortDesc: TotalCostDESC,
        sortKind: 'money',
        minWidth: 110,
        tooltip: 'Forecasted cost',
    },
    {
        display: 'Budget Variance',
        field: 'budget_variance',
        viewMode: 'budget',
        sortAsc: BudgetVarianceASC,
        sortDesc: BudgetVarianceDESC,
        sortKind: 'variance',
        minWidth: 110,
        tooltip: 'Budget variance',
    },
    {
        display: 'Approved Change Orders',
        field: 'approved_change_orders',
        viewMode: 'budget',
        sortAsc: ApprovedChangeOrdersASC,
        sortDesc: ApprovedChangeOrdersDESC,
        sortKind: 'count',
        minWidth: 80,
        tooltip: 'Approved change orders',
    },
    {
        display: 'Approved Change Order Cost',
        field: 'approved_change_order_cost',
        viewMode: 'budget',
        sortAsc: ApprovedChangeOrderCostASC,
        sortDesc: ApprovedChangeOrderCostDESC,
        sortKind: 'money',
        minWidth: 120,
        tooltip: 'Approved change order cost',
    },
    {
        display: 'Pending Change Orders',
        field: 'pending_change_orders',
        viewMode: 'budget',
        sortAsc: PendingChangeOrdersASC,
        sortDesc: PendingChangeOrdersDESC,
        sortKind: 'count',
        minWidth: 80,
        tooltip: 'Pending change orders',
    },
    {
        display: 'Pending Change Order Cost',
        field: 'pending_change_order_cost',
        viewMode: 'budget',
        sortAsc: PendingChangeOrderCostASC,
        sortDesc: PendingChangeOrderCostDESC,
        sortKind: 'money',
        minWidth: 120,
        tooltip: 'Pending change order cost',
    },
    {
        display: 'Actions',
        field: 'actions',
        viewMode: 'all',
        sortAsc: null,
        sortDesc: null,
        minWidth: 70,
        tooltip: 'Actions',
    },
]

const formatCurrency = (value: number, decimalPlaces: number = 2) => {
    if (value === null || value === undefined || isNaN(value)) {
        return '-'
    }
    const isNumber = typeof value === 'number' && !isNaN(value)
    if (!isNumber) {
        return '-'
    }
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
    }).format(value)
}

const formatInteger = (value: number) => {
    if (value === null || value === undefined || isNaN(value)) return '-'
    return value.toLocaleString('en-US')
}

const calculateBudgetVariance = (totalCost: number, baselineBudget: number) => {
    return baselineBudget - totalCost
}

// Render cell content based on header field
export const renderCellContent = (
    header: TableHeader,
    project: ProjectAnalytics,
    classes: any,
    handleMenuOpen: (
        event: React.MouseEvent<HTMLElement>,
        project: ProjectAnalytics,
    ) => void,
    theme: Theme,
    serviceLevel: boolean = false,
) => {
    switch (header.field) {
        case 'concern_level': {
            const concernLevel = project.concern_level
            let color = theme.palette.success.main
            if (concernLevel === 'MEDIUM') {
                color = theme.palette.warning.main
            } else if (concernLevel === 'HIGH') {
                color = theme.palette.error.main
            }
            return (
                <TableCell
                    style={{
                        textAlign: 'left',
                    }}
                    title={project.concern_level}
                >
                    <Badge
                        style={{
                            backgroundColor: color,
                            color: 'white',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            minWidth: '70px',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                        variant="dot"
                    >
                        {concernLevel}
                    </Badge>
                </TableCell>
            )
        }

        case 'apartment_name':
            return (
                <TableCell
                    style={{
                        textAlign: 'left',
                    }}
                    title={project.apartment_name}
                >
                    {project.apartment_name}
                </TableCell>
            )
        case 'name':
            return (
                <TableCell
                    style={{
                        textAlign: 'left',
                    }}
                    title={project.name}
                >
                    {project.name}
                </TableCell>
            )
        case 'start_date':
            return (
                <TableCell
                    style={{
                        textAlign: 'center',
                    }}
                >
                    {formatCalendarDateOnly(project.start_date)}
                </TableCell>
            )
        case 'end_date':
            return (
                <TableCell
                    style={{
                        textAlign: 'center',
                    }}
                >
                    {formatCalendarDateOnly(project.end_date)}
                </TableCell>
            )
        case 'total_workorders':
            return (
                <TableCell
                    style={{
                        textAlign: 'center',
                    }}
                >
                    {project.total_workorders}
                </TableCell>
            )
        case 'behind_count':
            return (
                <TableCell
                    style={{
                        textAlign: 'center',
                    }}
                >
                    {project.behind_count}
                </TableCell>
            )
        case 'not_started':
            return (
                <TableCell
                    style={{
                        textAlign: 'center',
                    }}
                >
                    {project.not_started}
                </TableCell>
            )
        case 'in_progress_count':
            return (
                <TableCell
                    style={{
                        textAlign: 'center',
                    }}
                >
                    {project.in_progress_count}
                </TableCell>
            )
        case 'complete_count':
            return (
                <TableCell
                    style={{
                        textAlign: 'center',
                    }}
                >
                    {project.complete_count}
                </TableCell>
            )
        case 'approved_count':
            return (
                <TableCell
                    style={{
                        textAlign: 'center',
                    }}
                >
                    {project.approved_count}
                </TableCell>
            )
        case 'invoiced_count':
            return (
                <TableCell
                    style={{
                        textAlign: 'center',
                    }}
                >
                    {project.invoiced_count}
                </TableCell>
            )
        case 'invoice_request_count':
            return (
                <TableCell
                    style={{
                        textAlign: 'center',
                    }}
                >
                    {project.invoice_request_count}
                </TableCell>
            )
        case 'invoiced_out_count':
            return (
                <TableCell
                    style={{
                        textAlign: 'center',
                    }}
                >
                    {project.invoiced_out_count}
                </TableCell>
            )
        case 'cai_count':
            return (
                <TableCell
                    style={{
                        textAlign: 'center',
                    }}
                >
                    {project.cai_count}
                </TableCell>
            )
        case 'unassigned_units':
            return (
                <TableCell
                    style={{
                        textAlign: 'center',
                    }}
                >
                    {project.unassigned_units}
                </TableCell>
            )
        case 'units_ready': {
            const unitsReadyScope =
                project.units_ready_scope_count ??
                project.units_need_service +
                    project.unique_units -
                    project.units_assigned
            const unitsReadyCount =
                project.units_ready_count ??
                unitsReadyScope -
                    project.unassigned_units -
                    project.units_with_pending_work
            const unitsReadyTooltip =
                unitsReadyScope > 0
                    ? `${unitsReadyCount}/${unitsReadyScope}`
                    : 'No units in scope for readiness'

            return (
                <Tooltip title={unitsReadyTooltip} key={header.field}>
                    <TableCell
                        style={{
                            textAlign: 'center',
                        }}
                    >
                        {project.units_ready !== null
                            ? `${project.units_ready}%`
                            : '-'}
                    </TableCell>
                </Tooltip>
            )
        }
        case 'units_scheduled': {
            const additionalUnitsAssigned =
                project.unique_units - project.units_assigned
            const hasScheduledDenominator = project.units_need_service > 0
            const percentPart = hasScheduledDenominator
                ? `${project.units_scheduled}%`
                : '-'
            const additionalPart =
                additionalUnitsAssigned > 0
                    ? ` (+${formatInteger(additionalUnitsAssigned)})`
                    : ''
            const percentLabel = `${percentPart}${additionalPart}`
            const tooltipTitle =
                additionalUnitsAssigned > 0 ? (
                    <>
                        {`${project.units_assigned}/${project.units_need_service}`}
                        <br />
                        <br />
                        {`${formatInteger(
                            additionalUnitsAssigned,
                        )} additional units scheduled.`}
                        <br />
                        <br />
                        Additional units scheduled are units which do not have
                        any area statuses which require service but have
                        workorders scheduled.
                    </>
                ) : (
                    `${project.units_assigned}/${project.units_need_service}`
                )

            return (
                <Tooltip title={tooltipTitle} key={header.field}>
                    <TableCell
                        style={{
                            textAlign: 'center',
                        }}
                    >
                        {percentLabel}
                    </TableCell>
                </Tooltip>
            )
        }
        case 'unit_turn_pct': {
            const turnEligible = project.turn_eligible_unit_count
            const propertyUnits = project.property_unit_count
            const hasTurnPct =
                project.unit_turn_pct !== null &&
                project.unit_turn_pct !== undefined
            const turnTooltip =
                propertyUnits && propertyUnits > 0
                    ? `${
                          turnEligible ?? 0
                      }/${propertyUnits} schedule-visible occupiable units`
                    : 'No schedule-visible occupiable units on this property'
            return (
                <Tooltip title={turnTooltip} key={header.field}>
                    <TableCell
                        style={{
                            textAlign: 'center',
                        }}
                    >
                        {hasTurnPct ? `${project.unit_turn_pct}%` : '—'}
                    </TableCell>
                </Tooltip>
            )
        }
        case 'max_vendor_output': {
            const output = project.max_vendor_output
            const hasOutput = output !== null && output !== undefined
            const warn = hasOutput && output > 10
            const serviceName = project.max_vendor_output_service_name
            const outputTooltip = hasOutput
                ? serviceName
                    ? `${output} WOs/day · ${serviceName}`
                    : `${output} WOs/day`
                : 'No paint/clean/carpet vendor–service pair with a usable work window (≥1 day)'
            return (
                <Tooltip title={outputTooltip} key={header.field}>
                    <TableCell
                        style={{
                            textAlign: 'center',
                            color: warn ? '#b45309' : undefined,
                            fontWeight: warn ? 600 : undefined,
                        }}
                    >
                        {hasOutput ? output.toFixed(1) : '—'}
                    </TableCell>
                </Tooltip>
            )
        }
        case 'unique_occupiable_areas':
            return (
                <TableCell
                    style={{
                        textAlign: 'center',
                    }}
                >
                    {project.unique_occupiable_areas}
                </TableCell>
            )
        case 'cost_per_occupiable_area':
            return (
                <Tooltip
                    title={`${formatCurrency(
                        project.total_cost,
                        0,
                    )} / ${formatInteger(project.unique_occupiable_areas)}`}
                    key={header.field}
                >
                    <TableCell
                        style={{
                            textAlign: 'center',
                        }}
                    >
                        {formatCurrency(project.cost_per_occupiable_area)}
                    </TableCell>
                </Tooltip>
            )

        case 'cost_per_unit':
            return (
                <Tooltip
                    title={`${formatCurrency(
                        project.total_cost,
                        0,
                    )} / ${formatInteger(project.unique_units)}`}
                    key={header.field}
                >
                    <TableCell
                        style={{
                            textAlign: 'center',
                        }}
                    >
                        {formatCurrency(project.cost_per_unit)}
                    </TableCell>
                </Tooltip>
            )
        case 'total_cost':
            return (
                <TableCell
                    style={{
                        textAlign: 'center',
                    }}
                >
                    {formatCurrency(project.total_cost, 0)}
                </TableCell>
            )
        case 'baseline_budget':
            return (
                <TableCell
                    style={{
                        textAlign: 'center',
                    }}
                >
                    {formatCurrency(project.baseline_budget, 0)}
                </TableCell>
            )
        case 'approved_change_orders':
            return (
                <TableCell
                    style={{
                        textAlign: 'center',
                    }}
                >
                    {project.approved_change_orders}
                </TableCell>
            )
        case 'pending_change_orders':
            return (
                <TableCell
                    style={{
                        textAlign: 'center',
                    }}
                >
                    {project.pending_change_orders}
                </TableCell>
            )
        case 'approved_change_order_cost':
            return (
                <TableCell
                    style={{
                        textAlign: 'center',
                    }}
                >
                    {formatCurrency(project.approved_change_order_cost, 0)}
                </TableCell>
            )
        case 'pending_change_order_cost':
            return (
                <TableCell
                    style={{
                        textAlign: 'center',
                    }}
                >
                    {formatCurrency(project.pending_change_order_cost, 0)}
                </TableCell>
            )
        case 'budget_variance':
            return (
                <TableCell
                    style={{
                        textAlign: 'center',
                    }}
                >
                    {formatCurrency(
                        calculateBudgetVariance(
                            project.total_cost,
                            project.baseline_budget,
                        ),
                        0,
                    )}
                </TableCell>
            )

        case 'actions':
            if (serviceLevel) {
                return <TableCell className={classes.cell} />
            }
            return (
                <TableCell className={classes.cell}>
                    <Tooltip title="Actions">
                        <IconButton onClick={(e) => handleMenuOpen(e, project)}>
                            <MoreVertIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </TableCell>
            )
        default:
            return <TableCell className={classes.cell} />
    }
}
